import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import humanTime from 'flarum/common/utils/humanTime';
import extractText from 'flarum/common/utils/extractText';
import icon from 'flarum/common/helpers/icon';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';
import Model from 'flarum/common/Model';
import Post from 'flarum/common/models/Post';
import User from 'flarum/common/models/User';
import PostControls from 'flarum/forum/utils/PostControls';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostUser from 'flarum/forum/components/PostUser';
import ReplyPlaceholder from 'flarum/forum/components/ReplyPlaceholder';
import TerminalPost from 'flarum/forum/components/TerminalPost';
import PostPreview from 'flarum/forum/components/PostPreview';
import PostsUserPage from 'flarum/forum/components/PostsUserPage';

import config from '../common/config';
import AnonymousAvatar from './components/AnonymousAvatar';
import classList from 'flarum/common/utils/classList';

export default function () {
  extend(CommentPost.prototype, 'headerItems', function (items) {
    const { post } = this.attrs;

    if (!post.attribute('isAnonymous')) {
      return;
    }

    items.setContent(
      'user',
      <div className="PostUser">
        <h3>
          <AnonymousAvatar className="PostUser-avatar" />
          <span className="username">{app.translator.trans(`${config.module.id}.lib.userMeta.username`)}</span>
        </h3>
      </div>
    );

    const anonymousUser = Model.hasOne<User>('anonymousUser').call(post);

    if (anonymousUser) {
      // Provide an altered post object that the PostUser component can read the user from
      const alteredPost = new Post({
        ...post.data,
        relationships: {
          ...post.data.relationships,
          user: post.data.relationships!.anonymousUser,
        },
      });

      items.add(
        'anonymousUser',
        <PostUser
          post={alteredPost}
          cardVisible={this.cardVisible}
          oncardshow={() => {
            this.cardVisible = true;
            m.redraw();
          }}
          oncardhide={() => {
            this.cardVisible = false;
            m.redraw();
          }}
        />,
        90 // Just after the original user label
      );
    } else if (post.attribute('isAnonymousMe')) {
      let className = 'AnonymousPostPrivacyMine';
      let tooltipText = app.translator.trans(`${config.module.id}.forum.postPrivacy.mineHelp`);
      let labelText = app.translator.trans(`${config.module.id}.forum.postPrivacy.mine`);

      if (app.current.matches(PostsUserPage)) {
        className = 'AnonymousPostPrivacyProfile';
        tooltipText = app.translator.trans(`${config.module.id}.forum.postPrivacy.profileHelp`);
        labelText = app.translator.trans(`${config.module.id}.forum.postPrivacy.profile`);
      }

      items.add(
        'anonymousUserPrivacy',
        <Tooltip text={tooltipText}>
          <span className={classList('AnonymousPostPrivacy', className)}>{labelText}</span>
        </Tooltip>,
        90
      );
    }
  });

  extend(CommentPost.prototype, 'oninit', function () {
    this.subtree!.check(() => {
      return this.attrs.post.attribute('isAnonymous');
    });
  });

  extend(ReplyPlaceholder.prototype, 'view', function (vdom0) {
    if (!app.composer.fields!.isAnonymous) {
      return;
    }

    if (!vdom0 || !Array.isArray(vdom0.children)) {
      return;
    }

    // Loop through .Post children
    vdom0.children.forEach((vdom1) => {
      if (!vdom1 || !Array.isArray(vdom1.children)) {
        return;
      }

      // Loop through .Post-header children
      vdom1.children.forEach((vdom2) => {
        if (!vdom2 || !Array.isArray(vdom2.children)) {
          return;
        }

        // Loop through .PostUser children
        vdom2.children.forEach((vdom3, index3) => {
          if (!vdom3 || !Array.isArray(vdom3.children)) {
            return;
          }

          // Loop through <h3> children
          vdom3.children.forEach((child, index) => {
            if (!child || !child.attrs || !child.attrs.className) {
              return;
            }

            // Replace preview avatar
            if (child.attrs.className.indexOf('PostUser-avatar') !== -1) {
              vdom3.children[index] = <AnonymousAvatar className="PostUser-avatar" />;
            }

            // Replace preview username
            if (child.attrs.className === 'username') {
              child.text = app.translator.trans(`${config.module.id}.lib.userMeta.username`);
            }
          });

          // Remove ul.PostUser-badges which would show the actor's badges
          if (vdom3.attrs && vdom3.attrs.className && vdom3.attrs.className.indexOf('PostUser-badges') !== -1) {
            vdom2.children.splice(index3, 1);
          }
        });
      });
    });
  });

  extend(PostPreview.prototype, 'view', function (vdom) {
    if (!this.attrs.post.attribute('isAnonymous')) {
      return;
    }

    vdom.children.forEach((preview) => {
      if (!preview || !preview.attrs || !preview.attrs.className || preview.attrs.className.indexOf('PostPreview-content') === -1) {
        return;
      }

      preview.children.forEach((child, index) => {
        if (child && child.attrs && child.attrs.className && child.attrs.className.indexOf('Avatar') === 0) {
          preview.children.splice(index, 1, <AnonymousAvatar />);
        }
      });
    });
  });

  // override(TerminalPost.prototype, 'view', function (original, ...args) {
  //   const discussion = this.attrs.discussion;
  //
  //   if (this.attrs.lastPost && discussion.replyCount()) {
  //     if (discussion.lastPostedUser()) {
  //       return original(...args);
  //     }
  //
  //     // Loading discussion.lastPost for the sole purpose of getting post.isAnonymous would be detrimental to performance
  //     // Instead we'll replace all "[deleted] replied" texts with a generic message that doesn't include a username
  //
  //     return (
  //       <span>
  //         {icon('fas fa-reply')}{' '}
  //         {app.translator.trans(`${config.module.id}.forum.discussionList.genericReplyText`, {
  //           ago: humanTime(discussion.lastPostedAt()),
  //         })}
  //       </span>
  //     );
  //   }
  //
  //   if (!discussion.attribute('isAnonymous')) {
  //     return original(...args);
  //   }
  //
  //   return (
  //     <span>
  //       {''}{' '}
  //       {app.translator.trans('core.forum.discussion_list.started_text', {
  //         username: <span className="username">{app.translator.trans(`${config.module.id}.lib.userMeta.username`)}</span>,
  //         ago: humanTime(discussion.createdAt()),
  //       })}
  //     </span>
  //   );
  // });

  extend(PostControls, 'moderationControls', function (items, post) {
    if (post.attribute('canDeAnonymize')) {
      items.add(
        'deanonymize',
        <Button
          icon="fas fa-user-secret"
          onclick={() => {
            if (!confirm(extractText(app.translator.trans(`${config.module.id}.forum.postControls.deanonymizeConfirmation`)))) {
              return;
            }

            post
              .save({
                isAnonymous: false,
              })
              .then(() => {
                m.redraw();
              });
          }}
        >
          {app.translator.trans(`${config.module.id}.forum.postControls.deanonymize`)}
        </Button>
      );
    }

    if (post.attribute('canAnonymize')) {
      items.add(
        'anonymize',
        <Button
          icon="fas fa-user-secret"
          onclick={() => {
            if (post.number() === 1) {
              if (!confirm(extractText(app.translator.trans(`${config.module.id}.forum.postControls.firstPostConfirmation`)))) {
                return;
              }
            }

            post
              .save({
                isAnonymous: true,
              })
              .then(() => {
                m.redraw();
              });
          }}
        >
          {app.translator.trans(`${config.module.id}.forum.postControls.anonymize`)}
        </Button>
      );
    }
  });
}
